import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  InitialPasswordNotice,
  Label,
  Modal,
  Notification,
  TextInput,
} from 'shared/components';
import { getApiErrorMessage } from 'shared/helpers';
import { validateEmail, debouncedValidateEmail } from 'shared/validation';
import {
  addOrderManager,
  patchLicenseUser,
  checkExistingUser,
} from 'orders/OrderContainer/actions';
import ExistingUserForm from '../ExistingUserForm';
import ManagerPermissionsPicker from '../ManagerPermissionsPicker';

const OrderManagerForm = ({
  closeCb,
  confirmCb,
  orderID,
  orderManagers,
  manager,
}) => {
  const { t } = useTranslation();
  const managerToEditID = get(manager, 'id');

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [email, setEmail] = useState(get(manager, 'true_email', ''));
  const [emailError, setEmailError] = useState('');
  const [firstName, setFirstName] = useState(get(manager, 'first_name', ''));
  const [lastName, setLastName] = useState(get(manager, 'last_name', ''));
  const [phoneNumber, setPhoneNumber] = useState(
    get(manager, 'phone_number', ''),
  );
  const [permissions, setPermissions] = useState(null);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  // existing user
  const [isExistingUserFormDisplayed, setExistingUserFormDisplay] =
    useState(null);

  const checkManagersList = (val) => {
    const managersEmailList = orderManagers.map((om) =>
      om.true_email.toLowerCase(),
    );
    const doesEmailExist = managersEmailList.includes(val.toLowerCase());
    return doesEmailExist;
  };

  const handleEmailChange = (val) => {
    const isManagerAlreadyAdded = checkManagersList(val);

    if (!manager && isManagerAlreadyAdded) {
      setDirty(true);
      setEmail(val);
      setEmailError('Manager with this email is already added to the list');
      return false;
    }

    setDirty(true);
    setEmail(val);
    debouncedValidateEmail(val).then((err) => setEmailError(err));
    return true;
  };

  const handleEmailValidation = async () => {
    const isManagerAlreadyAdded = checkManagersList(email);
    if (!manager && isManagerAlreadyAdded) {
      setEmailError('Manager with this email is already added to the list');
      return false;
    }

    setLoading(true);
    let errors;
    try {
      errors = await validateEmail(email);
      setEmailError(errors);
    } catch (err) {}
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const isPermissionsValid = () => {
    if (Array.isArray(permissions) && !permissions.length) {
      setPermissionsError(t('Select at least one permission'));
      return false;
    }
    setPermissionsError('');
    return true;
  };

  const isFormValid = async () => {
    const isEmailValid = await handleEmailValidation();
    return isPermissionsValid() && isEmailValid;
  };

  const handlePermissionsChange = (val) => {
    setDirty(true);
    setPermissions(val);
    setPermissionsError('');
  };

  const handleSubmitSuccess = () => {
    setExistingUserFormDisplay(false);
    confirmCb();
    Notification('success', t('Changes saved successfully'));
    return true;
  };

  const handleSubmitError = (err) => {
    setLoading(false);
    Notification(
      'error',
      t('Your changes were not saved'),
      getApiErrorMessage(err) ||
        t('There was an error while saving your changes'),
    );
    return false;
  };

  const updateExistingManager = (data) => {
    patchLicenseUser(managerToEditID, data)
      .then((res) => handleSubmitSuccess(res))
      .catch((err) =>
        handleSubmitError(err, data, 'Cannot update license manager'),
      );
  };

  const createNewManager = (data) => {
    addOrderManager(orderID, data)
      .then((res) => handleSubmitSuccess(res))
      .catch((err) =>
        handleSubmitError(err, data, 'Cannot create license manager'),
      );
  };

  const checkIfUserExisting = (data) => {
    checkExistingUser(email)
      .then((res) => {
        const isInUse = get(res, 'data.email_in_use');
        if (!isInUse) {
          createNewManager(data);
          return true;
        }

        setLoading(false);
        setExistingUserFormDisplay(true);
        return true;
      })
      .catch(() => {
        setLoading(false);
        Notification(
          'error',
          t('Error occured'),
          t('Your changes were not saved'),
        );
      });
  };

  const addExistingUserAsManager = () => {
    if (isLoading) {
      return false;
    }

    setLoading(true);
    const data = { email, permissions: permissions || undefined };
    createNewManager(data);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (permissionsLoading) {
      return false;
    }
    const isValid = await isFormValid();
    if (!isValid || isLoading) {
      return false;
    }

    setLoading(true);
    const data = {
      email,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      phone_number: phoneNumber || undefined,
    };

    if (!manager) {
      data.permissions = permissions || undefined;
      checkIfUserExisting(data);
    } else {
      updateExistingManager(data);
    }
    return true;
  };

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading || permissionsLoading}
      size="sm"
      title={manager ? t('Edit license manager') : t('Add license manager')}
    >
      <form className="OrderManagerForm" onSubmit={handleSubmit}>
        <div className="form-row">
          <Label text={t('Email')} inputId="email" />
          <TextInput
            id="email"
            disabled={!!manager}
            value={email}
            error={emailError}
            handleChange={handleEmailChange}
          />
        </div>
        <div className="form-row">
          <Label text={t('First Name')} inputId="firstname" />
          <TextInput
            id="firstname"
            value={firstName}
            handleChange={(val) => {
              setDirty(true);
              setFirstName(val);
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('Last Name')} inputId="lastname" />
          <TextInput
            id="lastname"
            value={lastName}
            handleChange={(val) => {
              setDirty(true);
              setLastName(val);
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('Phone number')} inputId="phone" />
          <TextInput
            id="phone"
            value={phoneNumber}
            handleChange={(val) => {
              setDirty(true);
              setPhoneNumber(val);
            }}
          />
        </div>
        {!manager && (
          <ManagerPermissionsPicker
            permissions={permissions}
            onChange={handlePermissionsChange}
            onLoadingChange={setPermissionsLoading}
            error={permissionsError}
            disabled={isLoading}
          />
        )}
        {!manager && <InitialPasswordNotice />}
      </form>
      {isExistingUserFormDisplayed && (
        <ExistingUserForm
          existingUser={email}
          closeCb={() => {
            setLoading(false);
            setExistingUserFormDisplay(false);
          }}
          confirmCb={addExistingUserAsManager}
          loading={isLoading}
        />
      )}
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

OrderManagerForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  orderID: PropTypes.number.isRequired,
  orderManagers: PropTypes.array,
  manager: PropTypes.object,
};

OrderManagerForm.defaultProps = {
  orderManagers: [],
  manager: null,
};

export default OrderManagerForm;
